import { Flex, Heading, Button, Textarea, Text } from '@chakra-ui/react';
import { useResource, useResourceRelationship } from 'datx-jsonapi-react';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';

import { VotingStars } from '@/components/ui/features/events-app/event/EventContent/components/VotingStars';
import { IToastType } from '@/enums/ToastType';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { useStores } from '@/hooks/useStores';
import { useToast } from '@/hooks/useToast';
import { queryLectureVote } from '@/queries/lecture-votes';
import { queryThinEventConfigurationRelationship } from '@/queries/thin-events';
import { EventConfiguration } from '@/resources/EventConfiguration';
import { LectureVotes } from '@/resources/LectureVotes';
import { ThinLecture } from '@/resources/ThinLecture';

interface IVotingForm {
  vote?: LectureVotes;
  lecture: ThinLecture;
  setChangeVote: (changeVote: boolean) => void;
}

export const VotingForm: FC<IVotingForm> = ({ lecture, vote, setChangeVote }) => {
  const { eventId } = useQueryParams<{ eventId }>({
    eventId: StringParam,
  });
  const toast = useToast();
  const { register, handleSubmit } = useForm({ defaultValues: { comment: vote?.comment || '' } });
  const { store } = useStores();

  const { mutate: mutateVote } = useResource(queryLectureVote(lecture.id));
  const { data: ec } = useResourceRelationship<EventConfiguration>(queryThinEventConfigurationRelationship(eventId));
  const [voteSelected, setVoteSelected] = useState<number | null>(vote?.value || null);

  async function onSubmit({ comment }: { comment: string }) {
    try {
      await store.request(`${ThinLecture.type}/${lecture.id}/vote`, 'POST', {
        data: {
          type: LectureVotes.type,
          attributes: {
            value: voteSelected,
            comment: comment,
          },
        },
      });
      mutateVote();
      setChangeVote(false);
    } catch (err) {
      toast({ status: IToastType.ERROR, title: 'Error submitting a vote' });
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Flex justifyContent="center" alignItems="center" w="full" h="full" mt={6}>
        <Flex flexDir="column" p={12} alignItems="center" w="451px" bg="white" borderRadius="md">
          <Heading textAlign="center" fontSize="lg" fontWeight="bold" lineHeight="2xl">
            {ec.lecturesVotingHeader || 'Please consider voting for this presentation'}
          </Heading>

          <VotingStars voteSelected={voteSelected} setVoteSelected={setVoteSelected} />

          {ec.lecturesVotingAllowComment && (
            <Text fontSize="sm" color="gray.600" mb={2} w="full">
              {ec.lecturesVotingDescription || 'Do you have any other suggestions or comments?'}
            </Text>
          )}
          {ec.lecturesVotingAllowComment && (
            <Textarea
              {...register('comment')}
              minH="50px"
              fontFamily="body"
              placeholder="Type your comment here"
              rows={3}
            />
          )}
          <Button type="submit" isDisabled={voteSelected === null} mt="10" size="lg" w="full">
            {ec.lecturesVotingActionText || 'submit vote'}
          </Button>
        </Flex>
      </Flex>
    </form>
  );
};
